import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, AlertTriangle, Calendar, Search, Filter, TrendingDown, TrendingUp } from 'lucide-react';
import api from '../services/api';
import { useToast, LoadingSkeleton, Tooltip } from '../components/ui';

export default function MB52StockOverview() {
  const { success, error: showError } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [stockTypeFilter, setStockTypeFilter] = useState('');
  const [plantFilter, setPlantFilter] = useState('');

  // Fetch stock summary
  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['warehouse-stock-summary'],
    queryFn: async () => {
      const response = await api.get('/qc/warehouse-stock/summary/');
      return response.data;
    }
  });

  // Fetch warehouse stock
  const { data: stockData, isLoading, refetch } = useQuery({
    queryKey: ['warehouse-stock', stockTypeFilter, plantFilter, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (stockTypeFilter) params.append('stock_type', stockTypeFilter);
      if (plantFilter) params.append('plant', plantFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.get(`/qc/warehouse-stock/?${params.toString()}`);
      return response.data.results || response.data;
    }
  });

  // Fetch low stock items
  const { data: lowStockData } = useQuery({
    queryKey: ['warehouse-stock-low'],
    queryFn: async () => {
      const response = await api.get('/qc/warehouse-stock/low-stock/');
      return response.data.results || response.data || [];
    }
  });

  // Fetch expiring soon items
  const { data: expiringData } = useQuery({
    queryKey: ['warehouse-stock-expiring'],
    queryFn: async () => {
      const response = await api.get('/qc/warehouse-stock/expiring-soon/');
      return response.data.results || response.data || [];
    }
  });

  const getStockTypeBadge = (stockType) => {
    const badges = {
      'UNRESTRICTED': 'bg-green-100 text-green-800',
      'BLOCKED': 'bg-orange-100 text-orange-800',
      'RESTRICTED': 'bg-yellow-100 text-yellow-800',
      'RETURNS': 'bg-red-100 text-red-800',
      'IN_TRANSIT': 'bg-blue-100 text-blue-800'
    };
    return badges[stockType] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">MB52 - Warehouse Stock Overview</h1>
        <p className="text-gray-600 mt-1">Real-time inventory levels across all storage locations</p>
      </div>

      {/* Summary Cards */}
      {isSummaryLoading ? (
        <LoadingSkeleton type="stats" />
      ) : summaryData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Materials</p>
                <p className="text-2xl font-bold text-gray-900">{summaryData.overall.total_materials}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Stock Items</p>
                <p className="text-2xl font-bold text-gray-900">{summaryData.overall.total_items}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{Number(summaryData.overall.total_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Low Stock Items</p>
                <p className="text-2xl font-bold text-orange-600">{lowStockData?.length || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </div>
      )}

      {/* Stock Type Summary */}
      {summaryData?.summary && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Stock by Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {summaryData.summary.map((item) => (
              <div key={item.stock_type} className="border rounded-lg p-3">
                <p className="text-sm text-gray-600 mb-1">{item.stock_type.replace('_', ' ')}</p>
                <p className="text-xl font-bold text-gray-900">{item.total_items} items</p>
                <p className="text-sm text-gray-500">
                  ₹{Number(item.total_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Low Stock Alert */}
        {lowStockData && lowStockData.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
              <h3 className="font-semibold text-orange-900">Low Stock Alerts</h3>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {lowStockData.slice(0, 5).map((item) => (
                <div key={item.id} className="text-sm">
                  <span className="font-medium">{item.material_code}</span>
                  <span className="text-gray-600"> - {item.quantity} {item.unit_of_measure}</span>
                </div>
              ))}
            </div>
            {lowStockData.length > 5 && (
              <p className="text-xs text-orange-700 mt-2">+{lowStockData.length - 5} more items</p>
            )}
          </div>
        )}

        {/* Expiring Soon Alert */}
        {expiringData && expiringData.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <Calendar className="w-5 h-5 text-red-600 mr-2" />
              <h3 className="font-semibold text-red-900">Expiring Soon (30 days)</h3>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {expiringData.slice(0, 5).map((item) => (
                <div key={item.id} className="text-sm">
                  <span className="font-medium">{item.material_code}</span>
                  <span className="text-gray-600"> - Exp: {new Date(item.expiry_date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
            {expiringData.length > 5 && (
              <p className="text-xs text-red-700 mt-2">+{expiringData.length - 5} more items</p>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
          <Filter className="w-4 h-4 mr-2" />
          Filter Stock
        </h3>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by material code or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={stockTypeFilter}
              onChange={(e) => setStockTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Stock Types</option>
              <option value="UNRESTRICTED">Unrestricted</option>
              <option value="BLOCKED">Blocked/Q-Stock</option>
              <option value="RESTRICTED">Restricted</option>
              <option value="RETURNS">Returns</option>
              <option value="IN_TRANSIT">In Transit</option>
            </select>
            <Tooltip content="UNRESTRICTED: Available for use | BLOCKED: Quality hold (Q-Stock) | RESTRICTED: Limited use | RETURNS: Returned materials | IN_TRANSIT: Moving between locations" />
          </div>

          <select
            value={plantFilter}
            onChange={(e) => setPlantFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Plants</option>
            <option value="1000">Plant 1000</option>
            <option value="2000">Plant 2000</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setStockTypeFilter('');
              setPlantFilter('');
            }}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Stock Details</h2>
          <p className="text-sm text-gray-600 mt-1">
            {stockData?.length || 0} items {searchTerm || stockTypeFilter || plantFilter ? '(filtered)' : ''}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Material
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plant / Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    Stock Type
                    <Tooltip content="Indicates the availability status and usage restrictions of the material" position="bottom" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    Batch
                    <Tooltip content="Production batch number for traceability and quality control" position="bottom" />
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Movement
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="p-0">
                    <LoadingSkeleton type="table" />
                  </td>
                </tr>
              ) : stockData && stockData.length > 0 ? (
                stockData.map((stock) => (
                  <tr key={stock.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">{stock.material_code}</div>
                        <div className="text-sm text-gray-500">{stock.material_description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stock.plant} / {stock.storage_location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStockTypeBadge(stock.stock_type)}`}>
                        {stock.stock_type_display}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stock.batch_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {Number(stock.quantity).toLocaleString()} {stock.unit_of_measure}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      ₹{Number(stock.total_value).toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stock.expiry_date ? (
                        <span className={new Date(stock.expiry_date) < new Date(Date.now() + 30*24*60*60*1000) ? 'text-red-600 font-medium' : ''}>
                          {new Date(stock.expiry_date).toLocaleDateString()}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stock.last_movement_date ? new Date(stock.last_movement_date).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    No stock records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
